import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
// Option A — FA5 fallback icons (the app is on @fortawesome/* 5.12.1). When we
// upgrade to Font Awesome 6, swap the five `icon:` values below to the exact
// icons in `fa6:` — this AREA_META block is the single place to change.
import {
  faTrafficLight,
  faIndustry,
  faTruckMoving,
  faSync,
  faPlug,
} from '@fortawesome/pro-regular-svg-icons';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { ProbabilityCategory } from '../../models/ProbabilityCategory';
import {
  getThyroidPatternInsight,
  ThyroidPatternInsight,
  ThyroidArea,
} from '../../models/thyroid-pattern-insights.generated';
import { resolveThyroidPattern, ThyroidMarkerStates, Tri } from '../../models/ThyroidAnalysis';
import { MUTED_PALETTE } from '../charts/chartPalette';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};
const conditionTitleStyle: React.CSSProperties = { color: 'black', textAlign: 'left' };
const conditionListStyle: React.CSSProperties = { paddingLeft: 0, marginLeft: 0, listStyle: 'none' };
const bulletStyle: React.CSSProperties = { display: 'inline-block', marginRight: '3px' };
const markerStyle: React.CSSProperties = { color: 'black', textAlign: 'right' };
const sectionLabelStyle: React.CSSProperties = { textAlign: 'left', fontWeight: 600, color: 'black', marginTop: '6px' };

type Marker = { direction: 'up' | 'down'; label: string };

// Per-area likelihood band — frames each area as a likelihood of dysfunction on
// the same 4-zone palette as the gauge (green→yellow→orange→red); the icon +
// label use a darker, legible shade of the matching gauge zone (BAND_PALETTE) —
// the bright gauge colors are too light to read on the panel. `score` is the
// needle position (0–100) on the gauge:
//   • highlyLikely (red)    — a detected pattern belongs to this area.
//   • likely       (orange) — an area marker is outside the lab range, no pattern.
//   • possible     (yellow) — an area marker is outside optimal (within lab), no pattern.
//   • lessLikely   (green)  — area markers present, all within optimal.
//   • notAssessed  (gray)   — none of the area's markers were measured.
type Band = 'lessLikely' | 'possible' | 'likely' | 'highlyLikely' | 'notAssessed';

// Two palettes for the same four zones (green→red) plus an unassessed gray:
//  • GAUGE_PALETTE — the bright fills on the severity bars (the SAME colors as the
//    results-page marker bar graphs) and the legend gauge arcs.
//  • BAND_PALETTE  — darker, legible shades of those hues, used to tint the
//    likelihood word (the bright bar colors wash out as small text).
const GAUGE_PALETTE = {
  ...MUTED_PALETTE,
  gray: '#e3e3e3',
};

const BAND_PALETTE = {
  green: '#3fa34d',
  yellow: '#c79a00', // gold-yellow
  orange: '#e3741a', // saturated orange — kept well clear of the gold above and the red below
  red: '#d8472f',
  gray: '#b7b7b7',
};

// Area icons are black; unassessed areas drop the icon to the muted
// BAND_PALETTE.gray so the row still reads as inactive.
const ICON_COLOR = '#000';

// `color` tints the legend chip; the row's likelihood label is a colored badge
// (`labelBg`/`labelFg`) styled like the MOO high/low labels — bright zone
// background with the matching dark/white text MOO uses.
const BAND_META: Record<
  Band,
  { label: string; color: string; labelBg: string; labelFg: string; score: number | null; meaning: string }
> = {
  lessLikely:   { label: 'less likely',   color: BAND_PALETTE.green,  labelBg: GAUGE_PALETTE.green,  labelFg: '#14521d', score: 25,   meaning: 'Markers tested, all within optimal.' },
  possible:     { label: 'possible',      color: BAND_PALETTE.yellow, labelBg: GAUGE_PALETTE.yellow, labelFg: '#2e2a00', score: 60,   meaning: 'A marker is outside optimal (still within lab range).' },
  likely:       { label: 'likely',        color: BAND_PALETTE.orange, labelBg: GAUGE_PALETTE.orange, labelFg: '#3d2700', score: 80,   meaning: 'A marker is outside the lab reference range.' },
  highlyLikely: { label: 'highly likely', color: BAND_PALETTE.red,    labelBg: GAUGE_PALETTE.red,    labelFg: '#fff',    score: 95,   meaning: 'A detected pattern points to this area.' },
  notAssessed:  { label: 'add markers',   color: BAND_PALETTE.gray,   labelBg: '#e6e6e6',           labelFg: '#777',    score: null, meaning: "This area's markers weren't tested yet." },
};

const BAR_HEIGHT = 14; // px — ranked-bar track/fill height

// The ranked bars double-encode severity: LENGTH = the band's score (0–100) and
// HUE = the matching gauge zone. BAND_HUE maps a band to its bright GAUGE_PALETTE
// fill (identical to the hero dial's zone colors, so a red bar means the same
// thing as the needle pointing to red).
const BAND_HUE: Record<Band, keyof typeof GAUGE_PALETTE> = {
  lessLikely: 'green',
  possible: 'yellow',
  likely: 'orange',
  highlyLikely: 'red',
  notAssessed: 'gray',
};

// Band → the same ProbabilityCategory vocabulary the glucose gauge uses, so the
// hero dial reads "… is Highly Likely (95%)" exactly like the other sections.
const BAND_CATEGORY: Record<Band, ProbabilityCategory> = {
  lessLikely: ProbabilityCategory.LESS_LIKELY,
  possible: ProbabilityCategory.POSSIBLE,
  likely: ProbabilityCategory.LIKELY,
  highlyLikely: ProbabilityCategory.HIGHLY_LIKELY,
  notAssessed: ProbabilityCategory.NA,
};

// Bar-track background. The app renders on a white report card (light-only — no
// dark theme), so the track is a light neutral with an inset hairline that keeps
// its edges legible on white and behind the lighter (yellow/green) fills.
const TRACK_BG = '#e9ecf1';
const TRACK_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.06)';

// Legend display order (least → most likely, then not-assessed).
const LEGEND_ORDER: Band[] = ['lessLikely', 'possible', 'likely', 'highlyLikely', 'notAssessed'];

const AREA_ORDER: ThyroidArea[] = ['Signaling', 'Production', 'Transport', 'Conversion', 'Utilization'];

// Markers that inform each area's gauge (spec §10.2). An area is "assessed" if
// any of its markers were measured; its band = the worst deviation among them.
const AREA_MARKERS: Record<ThyroidArea, Array<keyof BloodworkAnalysis>> = {
  Signaling: ['tsh'],
  Production: ['t4Free', 't4Total', 't3Total', 'tpoAb', 'tgAb'],
  Transport: ['tbg', 't3Uptake'],
  Conversion: ['reverseT3'],
  Utilization: ['t3Free'],
};

// Areas that only flag in ONE direction. Utilization is about a LOW active
// hormone (cells not getting usable FT3); a HIGH FT3 is overproduction, owned by
// Production — so a marker-driven Utilization flag only fires on low FT3. (A
// resolved high-FT3 Utilization pattern still flags via flaggedAreas.)
const AREA_FLAG_DIRECTION: Partial<Record<ThyroidArea, 'low' | 'high'>> = {
  Utilization: 'low',
};

// ── Cross-area rules ──────────────────────────────────────────────────────────
// The per-area likelihood is otherwise computed independently, which double-
// counts a marker that is really a downstream/compensatory *consequence* of
// another area. Each rule below, when its condition holds, DEMOTES the named
// area to 'lessLikely' — the axis is functioning; the abnormality is explained
// elsewhere — so it doesn't read as its own dysfunction.
//
// The pattern's own (highly-likely) area is never suppressed (areaBand checks
// flaggedAreas first), so e.g. inappropriate high TSH (SITSH → Signaling pattern
// 80010) is unaffected — only an *appropriate* high TSH is suppressed here.
//
// This list is expected to grow; keep each rule's `because` accurate for review.
const AREA_SUPPRESSION_RULES: Array<{
  when: (s: ThyroidMarkerStates) => boolean;
  suppress: ThyroidArea;
  because: string;
}> = [
  {
    // Positive thyroid antibodies = autoimmune Production disease; whatever the
    // TSH is doing (high compensating, or low) is secondary — not a Signaling
    // problem. (Inappropriate high TSH / SITSH makes Signaling the resolved
    // pattern area, which is never suppressed, so 80010 is unaffected.)
    when: (s) => s.abPos,
    suppress: 'Signaling',
    because: 'Positive thyroid antibodies (TPOAb/TgAb) indicate autoimmune Production — the TSH response is secondary, not a Signaling problem.',
  },
  {
    // Mirror: low TSH + high FT4/FT3 = overproduction; the low TSH is the
    // pituitary appropriately suppressing, not a Signaling problem. Central
    // hypothyroidism (low TSH + LOW FT4/FT3) is the 80000 Signaling pattern and
    // stays flagged (the pattern area is never suppressed).
    when: (s) => s.tsh === 'low' && (s.ft4 === 'high' || s.ft3 === 'high'),
    suppress: 'Signaling',
    because: 'Low TSH is the pituitary appropriately suppressing in response to thyroid-hormone overproduction (high FT4/FT3) — not a Signaling problem.',
  },
  {
    // High TSH + low FT4/FT3 = primary hypothyroidism (the gland under-producing);
    // the high TSH is the pituitary compensating, not a Signaling problem — with
    // OR without antibodies (rule ① covers the autoimmune case; this covers the
    // non-autoimmune one). Inappropriate high TSH (SITSH, high FT4/FT3) is the
    // 80010 Signaling pattern and stays flagged.
    when: (s) => s.tsh === 'high' && (s.ft4 === 'low' || s.ft3 === 'low'),
    suppress: 'Signaling',
    because: 'High TSH is the pituitary compensating for primary hypothyroidism (low FT4/FT3) — not a Signaling problem.',
  },
  {
    // Low FT3 driven by a Production deficit (autoimmune, or low FT4 output) is a
    // supply problem, not cellular under-use — hold Utilization. (Low FT3 from a
    // conversion block — normal FT4 + high rT3 — is NOT covered here; that stays
    // a Conversion story.)
    when: (s) => s.ft3 === 'low' && (s.abPos || s.ft4 === 'low'),
    suppress: 'Utilization',
    because: 'Low FT3 here is a Production supply effect (autoimmune / low FT4 output), not a Utilization (cellular use) problem.',
  },
];

// Centralized area metadata. `fa6` records the exact Font Awesome 6 icon to
// switch to after the @fortawesome upgrade (see spec §10.6). `iconSize` is an
// optional per-icon optical-size override (px), retained for the FA6 swap.
const AREA_META: Record<ThyroidArea, { label: string; icon: IconDefinition; fa6: string; iconSize?: number }> = {
  Signaling: { label: 'Signaling', icon: faTrafficLight, fa6: 'traffic-light', iconSize: 23 },
  Production: { label: 'Production', icon: faIndustry, fa6: 'industry-windows' },
  Transport: { label: 'Transport', icon: faTruckMoving, fa6: 'truck-moving' },
  Conversion: { label: 'Conversion', icon: faSync, fa6: 'arrows-rotate', iconSize: 22 },
  Utilization: { label: 'Utilization', icon: faPlug, fa6: 'plug-circle-bolt' },
};

// House display order for the per-pattern marker-arrow row.
const MARKER_ROW: Array<{ key: keyof BloodworkAnalysis; label: string }> = [
  { key: 'tsh', label: 'TSH' },
  { key: 't4Free', label: 'FT4' },
  { key: 't3Free', label: 'FT3' },
  { key: 't4Total', label: 'TT4' },
  { key: 't3Total', label: 'TT3' },
  { key: 'reverseT3', label: 'rT3' },
  { key: 'tbg', label: 'TBG' },
  { key: 't3Uptake', label: 'T3U' },
  { key: 'tpoAb', label: 'TPOAb' },
  { key: 'tgAb', label: 'TgAb' },
];

// marker key → abbreviated label, derived from MARKER_ROW so an area box can
// label its own markers without a second source of truth.
const MARKER_LABEL: Record<string, string> = MARKER_ROW.reduce(
  (acc, { key, label }) => ({ ...acc, [key as string]: label }),
  {} as Record<string, string>,
);

// The noun paired with each area name across the dial, the bars, and the boxes.
// Fixed in code (not read from the sheet's Pattern Title) so the three surfaces
// never disagree — flip this one value to change the wording everywhere.
const AREA_NOUN = 'Dysfunction';
const areaTitle = (area: ThyroidArea) => `${AREA_META[area].label} ${AREA_NOUN}`;

// One area's place in the ranked-bar chart: its likelihood band and the 0–100
// score that drives both the bar length and (for the hero) the dial needle.
type RankedArea = { area: ThyroidArea; band: Band; score: number };

type Props = { bloodworkAnalysis: BloodworkAnalysis };

export class ThyroidRegulationInsights extends React.Component<Props, {}> {
  // ── Null-safe marker classification (mirrors GlucoseRegulationInsights) ─────
  markerIsHigh = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isGreaterThanOptimal?.());
  markerIsLow = (m: any) =>
    !!(m?.isLessThanLab?.() || m?.isStricklyLessThanOptimal?.() || m?.isLessThanOptimal?.());
  markerHasValue = (m: any) => !!m?.hasValue?.();
  // Granularity for the area likelihood ladder: outside the lab reference range
  // vs. merely outside optimal (still within lab). `dir` restricts which side
  // counts (for areas that only flag one way, e.g. Utilization on low FT3).
  markerOutsideLab = (m: any, dir?: 'low' | 'high') => {
    if (dir === 'low') return !!m?.isLessThanLab?.();
    if (dir === 'high') return !!m?.isGreaterThanLab?.();
    return !!(m?.isGreaterThanLab?.() || m?.isLessThanLab?.());
  };
  markerOutsideOptimal = (m: any, dir?: 'low' | 'high') => {
    if (dir === 'low') return !!(m?.isLessThanOptimal?.() || m?.isStricklyLessThanOptimal?.());
    if (dir === 'high') return !!m?.isGreaterThanOptimal?.();
    return !!(m?.isGreaterThanOptimal?.() || m?.isLessThanOptimal?.() || m?.isStricklyLessThanOptimal?.());
  };

  triState = (m: any): Tri => {
    if (!this.markerHasValue(m)) return 'absent';
    if (this.markerIsHigh(m)) return 'high';
    if (this.markerIsLow(m)) return 'low';
    return 'opt';
  };

  buildStates = (ba: BloodworkAnalysis): ThyroidMarkerStates => ({
    tsh: this.triState(ba.tsh),
    ft4: this.triState(ba.t4Free),
    ft3: this.triState(ba.t3Free),
    rt3: this.triState(ba.reverseT3),
    tt4: this.triState(ba.t4Total),
    tt3: this.triState(ba.t3Total),
    tbg: this.triState(ba.tbg),
    t3u: this.triState(ba.t3Uptake),
    abPos: this.markerIsHigh(ba.tpoAb) || this.markerIsHigh(ba.tgAb),
  });

  buildMarkerArrows = (ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const { key, label } of MARKER_ROW) {
      const m = (ba as any)[key];
      if (this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // Areas whose flag is explained by a cross-area rule (see AREA_SUPPRESSION_RULES).
  suppressedAreas = (ba: BloodworkAnalysis): Set<ThyroidArea> => {
    const states = this.buildStates(ba);
    const set = new Set<ThyroidArea>();
    for (const rule of AREA_SUPPRESSION_RULES) {
      if (rule.when(states)) set.add(rule.suppress);
    }
    return set;
  };

  // ── 5-Area likelihood band ──────────────────────────────────────────────────
  // Ladder: detected pattern → highly likely; marker outside lab range → likely;
  // marker outside optimal (within lab) → possible; markers present & optimal →
  // less likely; no markers measured → not assessed. A cross-area rule can then
  // demote a possible/likely area to less-likely when its marker is explained by
  // another area (the pattern/highly-likely area itself is never suppressed).
  areaBand = (
    area: ThyroidArea,
    ba: BloodworkAnalysis,
    flaggedAreas: Set<ThyroidArea>,
    suppressed?: Set<ThyroidArea>,
  ): Band => {
    if (flaggedAreas.has(area)) return 'highlyLikely';
    const keys = AREA_MARKERS[area];
    if (!keys.some((key) => this.markerHasValue((ba as any)[key]))) return 'notAssessed';
    const dir = AREA_FLAG_DIRECTION[area];
    const raw: Band = keys.some((key) => this.markerOutsideLab((ba as any)[key], dir))
      ? 'likely'
      : keys.some((key) => this.markerOutsideOptimal((ba as any)[key], dir))
      ? 'possible'
      : 'lessLikely';
    if (suppressed?.has(area) && raw !== 'lessLikely') return 'lessLikely';
    return raw;
  };

  // ── Rendering ───────────────────────────────────────────────────────────────
  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={direction === 'up' ? '↑' : '↓'} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Rank the five areas worst→best for the ranked-bar chart. `score` is the
  // band's needle position (0–100); not-assessed areas get score 0 and sort to
  // the bottom (sortKey −1). Sort is stable, so equal scores keep AREA_ORDER.
  rankedAreas = (ba: BloodworkAnalysis, flaggedAreas: Set<ThyroidArea>): RankedArea[] => {
    const suppressed = this.suppressedAreas(ba);
    return AREA_ORDER.map((area) => {
      const band = this.areaBand(area, ba, flaggedAreas, suppressed);
      const score = BAND_META[band].score; // number | null (null = not assessed)
      return { area, band, score: score ?? 0, sortKey: score ?? -1 };
    })
      .sort((a, b) => b.sortKey - a.sortKey)
      .map(({ area, band, score }) => ({ area, band, score }));
  };

  // ── "How to read this" legend (rendered inside the title's info Popover) ──────
  renderLikelihoodLegend = () => (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
        How likely each physiological area is involved.
      </div>
      {LEGEND_ORDER.map((band, i) => {
        const meta = BAND_META[band];
        return (
          <div
            key={band}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '5px 0',
              borderTop: i === 0 ? 'none' : '1px solid #f0f0f0',
            }}
          >
            {/* mini ranked-bar swatch — same length=score / hue=zone encoding as
                the panel's bars, so the legend reads in the same visual language */}
            <div
              style={{
                position: 'relative',
                width: '56px',
                height: '10px',
                borderRadius: '3px',
                background: TRACK_BG,
                boxShadow: TRACK_INSET,
                flexShrink: 0,
              }}
            >
              {band !== 'notAssessed' && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${meta.score}%`,
                    background: GAUGE_PALETTE[BAND_HUE[band]],
                    borderRadius: '3px',
                  }}
                />
              )}
            </div>
            <span
              style={{
                background: '#f5f5f5',
                borderRadius: '6px',
                padding: '3px 9px',
                fontSize: '12.5px',
                fontWeight: 700,
                color: meta.color,
                whiteSpace: 'nowrap',
                minWidth: '84px',
                textAlign: 'center',
              }}
            >
              {meta.label}
            </span>
            <span style={{ fontSize: '11.5px', color: '#555', lineHeight: 1.25 }}>{meta.meaning}</span>
          </div>
        );
      })}
    </div>
  );

  likelihoodLegendPopover = () => (
    <Popover id="thyroid-likelihood-legend" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        5-Area Likelihood
      </Popover.Title>
      <Popover.Content>{this.renderLikelihoodLegend()}</Popover.Content>
    </Popover>
  );

  // Hero headline — the single worst (top-ranked) area, as a one-line summary.
  // Reuses the SAME label styling the other sections' gauges use (the
  // anemiaStatusGuageLabel* classes) so it reads exactly like their gauge caption
  // — "Production Dysfunction is / Highly Likely (95%)" — minus the semicircle,
  // which only duplicated the top ranked bar. Title via `areaTitle`, the same
  // helper the boxes use, so headline and boxes never disagree.
  renderHeroHeadline = (hero: RankedArea) => (
    <div className="anemiaStatusGuageLabelContainer" style={{ padding: '2px 4px 10px' }}>
      <span style={{ fontWeight: 700 }}>{areaTitle(hero.area)} is</span>
      <div className="anemiaStatusGuageLabel">
        {BAND_CATEGORY[hero.band]} ({hero.score}%)
      </div>
    </div>
  );

  // Ranked severity bars — all five areas on a shared left baseline, sorted
  // worst→best so the set reads as one descending shape at a glance. Bar LENGTH =
  // likelihood score and HUE = the matching gauge zone (double-encoded, so length
  // still carries the meaning if hue is lost). The hero row (top-ranked — the area
  // the dial above points to) is tinted + bolded to connect the two elements.
  renderRankedBars = (ranked: RankedArea[], heroArea: ThyroidArea) => (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
        The 5-Area Thyroid Framework&trade;
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {ranked.map(({ area, band, score }) => {
          const meta = BAND_META[band];
          const assessed = band !== 'notAssessed';
          const isHero = area === heroArea;
          return (
            <div
              key={area}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 6px',
              }}
            >
              <FontAwesomeIcon
                icon={AREA_META[area].icon}
                style={{
                  fontSize: '14px',
                  width: '18px',
                  flexShrink: 0,
                  color: assessed ? ICON_COLOR : BAND_PALETTE.gray,
                }}
              />
              <span
                style={{
                  width: '62px',
                  flexShrink: 0,
                  fontSize: '11.5px',
                  fontWeight: isHero ? 700 : 600,
                  color: assessed ? '#333' : BAND_PALETTE.gray,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {AREA_META[area].label}
              </span>
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  height: `${BAR_HEIGHT}px`,
                  borderRadius: '4px',
                  background: TRACK_BG,
                  boxShadow: TRACK_INSET,
                }}
              >
                {assessed && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${score}%`,
                      minWidth: '6px',
                      background: GAUGE_PALETTE[BAND_HUE[band]],
                      borderRadius: '4px',
                    }}
                  />
                )}
                {/* endpoint cap — marks exactly where the bar lands */}
                {assessed && (
                  <span
                    style={{
                      position: 'absolute',
                      left: `${score}%`,
                      top: '-1px',
                      bottom: '-1px',
                      width: '2px',
                      borderRadius: '1px',
                      transform: 'translateX(-100%)',
                      background: BAND_PALETTE[BAND_HUE[band]],
                    }}
                  />
                )}
              </div>
              <span
                style={{
                  width: '30px',
                  flexShrink: 0,
                  textAlign: 'right',
                  fontSize: '11px',
                  fontWeight: isHero ? 700 : 600,
                  color: assessed ? meta.color : BAND_PALETTE.gray,
                }}
              >
                {assessed ? `${score}%` : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  renderBulletSection = (label: string, items: string[]) => (
    <div style={{ textAlign: 'left' }}>
      <div style={sectionLabelStyle}>{label}</div>
      <ul style={conditionListStyle}>
        {items.map((item) => (
          <li key={item} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span style={bulletStyle}>&bull;</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  // The deviating markers that belong to one area (its own AREA_MARKERS), so each
  // box surfaces just the markers driving that area — together the boxes tile the
  // full pattern signature without repeating a marker across boxes.
  areaMarkerArrows = (area: ThyroidArea, ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const key of AREA_MARKERS[area]) {
      const label = MARKER_LABEL[key as string];
      if (!label) continue;
      const m = (ba as any)[key];
      if (this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // Likelihood chip — the ProbabilityCategory word on the band's bright gauge hue
  // (red gets white text), matching the dial needle and the bar for that area.
  renderChip = (band: Band) => (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        borderRadius: '10px',
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        background: GAUGE_PALETTE[BAND_HUE[band]],
        color: band === 'highlyLikely' ? '#fff' : '#3a3a3a',
      }}
    >
      {BAND_CATEGORY[band]}
    </span>
  );

  renderMarkerRow = (markers: Marker[]) => (
    <div style={markerStyle}>
      {markers.map((marker, index) => (
        <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
          {index > 0 && <>,{' '}</>}
          {this.renderArrow(marker.direction, marker.label)}
        </React.Fragment>
      ))}
    </div>
  );

  // The resolved pattern's marker signature (sheet Logic column), filtered to the
  // markers actually abnormal in this patient and in Logic order — e.g. 80110 →
  // ↑TSH, ↓FT3, ↑TPOAb, ↑TgAb (FT4 omitted when optimal). This spans areas (TSH
  // is Signaling's, FT3 is Utilization's) on purpose: the pattern box shows the
  // whole pattern, while the minimal boxes show only their own area's markers.
  patternMarkerArrows = (insight: ThyroidPatternInsight, ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const { label, key, dirs } of insight.logic) {
      const m = (ba as any)[key];
      if (dirs.includes('high') && this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (dirs.includes('low') && this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // One box per elevated area. The resolved-pattern area (insight passed) shows
  // the full "Consistent with" + "Possible Root Causes" content and the pattern's
  // signature markers; every other area ≥50% shows the minimal box — title,
  // likelihood chip, and its own area's driving markers.
  renderAreaBox = (r: RankedArea, ba: BloodworkAnalysis, insight?: ThyroidPatternInsight) => {
    let markers = this.areaMarkerArrows(r.area, ba);
    if (insight && insight.logic.length) {
      const signature = this.patternMarkerArrows(insight, ba);
      if (signature.length) markers = signature;
    }
    return (
      <div className="condition-section" key={r.area}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ ...conditionTitleStyle, fontWeight: 700, fontSize: '14px' }}>{areaTitle(r.area)}</span>
          {this.renderChip(r.band)}
        </div>
        {insight && insight.bullets.length > 0 && this.renderBulletSection('Consistent with:', insight.bullets)}
        {markers.length > 0 && this.renderMarkerRow(markers)}
        {insight && insight.rootCauses.length > 0 && this.renderBulletSection('Possible Root Causes:', insight.rootCauses)}
      </div>
    );
  };

  // A box for every area at or above 50% (possible / likely / highly likely),
  // most-likely first — so one resolved pattern no longer hides the other areas
  // its markers lit up. Areas below 50% and not-assessed areas get no box.
  renderAreaBoxes = (ranked: RankedArea[], ba: BloodworkAnalysis, insight?: ThyroidPatternInsight) =>
    ranked
      .filter((r) => r.score >= 50)
      .map((r) => this.renderAreaBox(r, ba, insight && insight.area === r.area ? insight : undefined));

  // ── PDF pagination ──────────────────────────────────────────────────────────
  //
  // Added 2026-08-21 when this panel gained a PDF counterpart. The packer needs
  // a height BEFORE React renders, and a panel left out of the estimate makes
  // it think the next section still fits — that section's title then prints at
  // the bottom of this page and its rows spill onto the next.
  //
  // Both statics delegate to exportInsights(), which already mirrors render()
  // exactly (same gating, same 5-area bands, same resolved pattern) and returns
  // null when the panel would not show. Reusing it is what stops the estimate
  // and the render from drifting apart, which is the failure mode a duplicated
  // copy of the gating logic invites.
  /** Whether this panel renders anything — at least one thyroid marker measured. */
  static shouldRender(ba: BloodworkAnalysis): boolean {
    return ThyroidRegulationInsights.exportInsights(ba) !== null;
  }

  /*
   * Every constant below was MEASURED off the rendered panel at its real 300px
   * width (headless Chrome over the compiled custom.scss). The set it replaces
   * was short by 119-298px — the worst of the self-estimating panels — because
   * it costed the five ranked bars at 110 where they render 166, gave a whole
   * area box 28 for "title + chip row" when the title alone wraps to two 21px
   * lines, billed 16 for an 18px bullet line, never wrapped a bullet, never
   * counted the "Consistent with:" / "Possible Root Causes:" labels or the
   * 1rem bottom margin Bootstrap puts under each of their lists, and left out
   * the card chrome entirely.
   *
   * That under-count is what pushed Digestive & Gut Health down its page and
   * split it: THYROID sits immediately above it in the packer, so a short
   * thyroid unit leaves the page model wrong for everything below.
   */
  // Card header + .card-body padding + the 5px panel border, top and bottom.
  static readonly PDF_CARD_CHROME = 105;
  // .anemiaStatusContainer's min-height, which only binds if the panel is ever
  // made shorter than its headline and bars.
  static readonly PDF_MIN_BODY_HEIGHT = 150;
  // "{Area} Dysfunction is / {Likelihood} (n%)" — two lines at 13px.
  static readonly PDF_HEADLINE_HEIGHT = 48;
  // "The 5-Area Thyroid Framework™" plus the five ranked bars. Fixed: there are
  // always exactly five areas.
  static readonly PDF_RANKED_BARS_HEIGHT = 166;
  // Bootstrap's body line-height (1.5) at the panel's 12px font, and at the
  // 14px of an area box's title.
  static readonly PDF_LINE_HEIGHT = 18;
  static readonly PDF_TITLE_LINE_HEIGHT = 21;
  // .condition-section: 3px border and 5px padding, top and bottom.
  static readonly PDF_BOX_CHROME = 16;
  // .condition-section's `margin: 10px 0`, which collapses to one 10 between
  // adjacent boxes and above the first.
  static readonly PDF_BOX_MARGIN = 10;
  // A bullet section costs its label line (18) plus that label's 6px marginTop,
  // plus the 1rem bottom margin Bootstrap's reboot puts under the ul and
  // conditionListStyle does not reset.
  static readonly PDF_BULLET_SECTION_CHROME = 40;
  // Average glyph width at the panel's 12px font and at an area title's 14px
  // bold. Measured at 6.0 / 7.4; carried wide so the wrap arithmetic rounds up.
  static readonly PDF_CHAR_WIDTH = 6.2;
  static readonly PDF_TITLE_CHAR_WIDTH = 7.6;
  // Text width inside an area box, and inside one of its bullets (which also
  // gives up its glyph and the 3px margin after it).
  static readonly PDF_BOX_WIDTH = 244;
  static readonly PDF_BULLET_WIDTH = 234;
  // The likelihood chip's own padding plus the flex gap before it.
  static readonly PDF_CHIP_PADDING = 24;
  // Per marker in the ▲/▼ row: the glyph with its margins, plus the ", ".
  static readonly PDF_MARKER_SLOT_WIDTH = 13;
  static readonly PDF_MARKER_SEPARATOR_CHARS = 2;

  /** Rendered lines a string wraps to in `width` px at the given glyph width. */
  static pdfLines(
    text: string,
    width: number,
    charWidth: number = ThyroidRegulationInsights.PDF_CHAR_WIDTH,
  ): number {
    return Math.max(1, Math.ceil((text.length * charWidth) / width));
  }

  /** A "Consistent with:" / "Possible Root Causes:" block, or nothing. */
  static bulletSectionHeight(items?: string[]): number {
    const T = ThyroidRegulationInsights;
    if (!items || !items.length) return 0;
    return (
      T.PDF_BULLET_SECTION_CHROME +
      items.reduce(
        (h, item) => h + T.pdfLines(item, T.PDF_BULLET_WIDTH) * T.PDF_LINE_HEIGHT,
        0,
      )
    );
  }

  /** The ▲/▼ row, which wraps between whole markers. */
  static markerRowHeight(markers: Marker[]): number {
    const T = ThyroidRegulationInsights;
    if (!markers.length) return 0;
    const chars =
      markers.reduce((n, m) => n + m.label.length, 0) +
      (markers.length - 1) * T.PDF_MARKER_SEPARATOR_CHARS;
    const width =
      markers.length * T.PDF_MARKER_SLOT_WIDTH + chars * T.PDF_CHAR_WIDTH;
    return Math.max(1, Math.ceil(width / T.PDF_BOX_WIDTH)) * T.PDF_LINE_HEIGHT;
  }

  /**
   * One area box, including the margin above it. The title shares its line with
   * the likelihood chip, so a long area name and a long band word together are
   * what push it onto a second line — "Production Dysfunction" fits beside
   * "Likely" and wraps beside "Highly Likely".
   */
  static areaBoxHeight(box: {
    band: Band;
    title: string;
    markers: Marker[];
    bullets?: string[];
    rootCauses?: string[];
  }): number {
    const T = ThyroidRegulationInsights;
    const chip = String(BAND_CATEGORY[box.band] ?? '');
    const titleWidth =
      box.title.length * T.PDF_TITLE_CHAR_WIDTH +
      chip.length * T.PDF_CHAR_WIDTH +
      T.PDF_CHIP_PADDING;
    const titleLines = Math.max(1, Math.ceil(titleWidth / T.PDF_BOX_WIDTH));
    return (
      T.PDF_BOX_MARGIN +
      T.PDF_BOX_CHROME +
      titleLines * T.PDF_TITLE_LINE_HEIGHT +
      T.bulletSectionHeight(box.bullets) +
      T.markerRowHeight(box.markers) +
      T.bulletSectionHeight(box.rootCauses)
    );
  }

  /**
   * This panel's rendered height, in the capture pixels every other number
   * in the PDF's page arithmetic uses (see pdfPanelMetrics).
   */
  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    const T = ThyroidRegulationInsights;
    const view = T.exportInsights(ba);
    if (!view) return 0;
    const body =
      T.PDF_HEADLINE_HEIGHT +
      T.PDF_RANKED_BARS_HEIGHT +
      view.boxes.reduce((sum, box) => sum + T.areaBoxHeight(box), 0);
    return T.PDF_CARD_CHROME + Math.max(body, T.PDF_MIN_BODY_HEIGHT);
  }


  // Serializable view of the Thyroid Insights panel for a single test, mirroring
  // render() exactly (same gating, same 5-area bands, same resolved pattern).
  // Used by the raw-data JSON export. Returns null when the panel wouldn't show.
  static exportInsights(ba: BloodworkAnalysis): {
    areas: Array<{ area: ThyroidArea; band: Band }>;
    boxes: Array<{
      area: ThyroidArea;
      band: Band;
      title: string;
      markers: Marker[];
      categoryId?: number;
      bullets?: string[];
      rootCauses?: string[];
    }>;
    pattern?: {
      categoryId: number;
      title: string;
      bullets: string[];
      rootCauses: string[];
      area: ThyroidArea;
      markers: Marker[];
    };
  } | null {
    if (!ba?.bloodwork) return null;
    const inst = new ThyroidRegulationInsights({ bloodworkAnalysis: ba });
    const hasAnyThyroidMarker = MARKER_ROW.some(({ key }) =>
      inst.markerHasValue((ba as any)[key]),
    );
    if (!hasAnyThyroidMarker) return null;

    const states = inst.buildStates(ba);
    const categoryId = resolveThyroidPattern(states);
    const insight = categoryId != null ? getThyroidPatternInsight(categoryId) : undefined;

    const flaggedAreas = new Set<ThyroidArea>();
    if (insight) flaggedAreas.add(insight.area);

    // Mirror the panel: one box per area ≥50%, most-likely first.
    const ranked = inst.rankedAreas(ba, flaggedAreas);
    const boxes = ranked
      .filter((r) => r.score >= 50)
      .map((r) => {
        const isPatternArea = !!insight && insight.area === r.area;
        const signature = isPatternArea && insight!.logic.length ? inst.patternMarkerArrows(insight!, ba) : [];
        return {
          area: r.area,
          band: r.band,
          title: areaTitle(r.area),
          markers: signature.length ? signature : inst.areaMarkerArrows(r.area, ba),
          ...(isPatternArea && categoryId != null
            ? { categoryId, bullets: insight!.bullets, rootCauses: insight!.rootCauses }
            : {}),
        };
      });

    const suppressed = inst.suppressedAreas(ba);
    const result: {
      areas: Array<{ area: ThyroidArea; band: Band }>;
      boxes: typeof boxes;
      pattern?: any;
    } = {
      areas: AREA_ORDER.map(area => ({ area, band: inst.areaBand(area, ba, flaggedAreas, suppressed) })),
      boxes,
    };
    if (insight && categoryId != null) {
      result.pattern = {
        categoryId,
        title: areaTitle(insight.area),
        bullets: insight.bullets,
        rootCauses: insight.rootCauses,
        area: insight.area,
        markers: inst.buildMarkerArrows(ba),
      };
    }
    return result;
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis.bloodwork) return null;
    const ba = bloodworkAnalysis;

    // Render only if at least one thyroid marker was measured.
    const hasAnyThyroidMarker = MARKER_ROW.some(({ key }) => this.markerHasValue((ba as any)[key]));
    if (!hasAnyThyroidMarker) return null;

    // Single source of truth: the same pure resolver the model uses.
    const states = this.buildStates(ba);
    const categoryId = resolveThyroidPattern(states);
    const insight = categoryId != null ? getThyroidPatternInsight(categoryId) : undefined;

    const flaggedAreas = new Set<ThyroidArea>();
    if (insight) flaggedAreas.add(insight.area);

    // Worst→best ranking drives the hero dial (top-ranked), the bars, and the
    // per-area boxes. At least one thyroid marker is measured (guard above) and
    // every marker maps to an area, so ranked[0] is always an assessed area.
    const ranked = this.rankedAreas(ba, flaggedAreas);
    const hero = ranked[0];

    return (
      <Card className="evaluationPanel" id="thyroid-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Thyroid Insights
          <OverlayTrigger placement="bottom" overlay={this.likelihoodLegendPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {hero.band !== 'notAssessed' && this.renderHeroHeadline(hero)}
            {this.renderRankedBars(ranked, hero.area)}
            {this.renderAreaBoxes(ranked, ba, insight)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
